import React, { useState, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { useAppDispatch, useAppSelector } from '../../../store/index.ts'
import { addGuest as addGuestAction, updateGuest, removeGuest } from '../../../store/seatingSlice.ts'
import { createGuest, updateGuestEndpoint, deleteGuestEndpoint } from '../../../endpoint/index.ts'
import { rsvpApproveGuestEndpoint } from '../../../endpoint/rsvp.ts'
import { IGuest } from '../../../interface/index.ts'
import { Modal, Form, Button } from 'react-bootstrap'
import './index.css'

interface Props {
	guests: IGuest[];
	plannerId: string;
	isMobile?: boolean;
}

interface DraggableGuestProps {
	guest: IGuest;
	onEdit: (guest: IGuest) => void;
	onDelete: (guestId: string) => void;
	onApprove: (guest: IGuest) => void;
	isMobile?: boolean;
}

const DraggableGuest: React.FC<DraggableGuestProps> = ({ guest, onEdit, onDelete, onApprove, isMobile }) => {
	const { t } = useTranslation('planner')
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: `guest-${guest.id}`,
		data: { type: 'guest', guest },
		disabled: isMobile // Disable drag on mobile - use tap-to-assign instead
	})

	// Detect touch device
	const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

	const style = {
		transform: CSS.Translate.toString(transform),
		opacity: isDragging ? 0.5 : 1
	}

	const getRsvpIcon = () => {
		switch (guest.rsvpStatus) {
			case 'confirmed': return '✓'
			case 'declined': return '✗'
			default: return '○'
		}
	}

	const getRsvpClass = () => {
		return `rsvp-icon rsvp-${guest.rsvpStatus}`
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`guest-item ${guest.tableId ? 'assigned' : ''} ${isDragging ? 'dragging' : ''} ${isTouchDevice || isMobile ? 'touch-device' : ''}`}
		>
			{!isMobile && (
				<div className='guest-drag-handle' {...listeners} {...attributes}>
					<span className='drag-icon'>⋮⋮</span>
				</div>
			)}
			<span className={getRsvpClass()}>{getRsvpIcon()}</span>
			<span className='guest-name'>{guest.firstName} {guest.lastName}</span>
			{guest.tableId && <span className='guest-table-badge'>{t('guestList.seated')}</span>}
			{guest.addedByGuest && (
				<span className='guest-added-badge'>{t('guestList.addedByGuest')}</span>
			)}
			<div className='guest-actions'>
				{guest.addedByGuest && (
					<button
						className='guest-action-btn approve'
						onClick={() => onApprove(guest)}
						title={t('guestList.approve')}
					>
						✓
					</button>
				)}
				<button className='guest-action-btn' onClick={() => onEdit(guest)} title={t('guestList.edit')}>
					✎
				</button>
				<button className='guest-action-btn delete' onClick={() => onDelete(guest.id)} title={t('guestList.delete')}>
					×
				</button>
			</div>
		</div>
	)
}

const GuestListPanel = forwardRef<HTMLDivElement, Props>(({ guests, plannerId, isMobile }, ref) => {
	const { t } = useTranslation('planner')
	const dispatch = useAppDispatch()
	const { authenticatedPlanner } = useAppSelector(({ seating }) => seating)

	const [searchTerm, setSearchTerm] = useState<string>('')
	const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('all')
	const [showModal, setShowModal] = useState<boolean>(false)
	const [editingGuest, setEditingGuest] = useState<IGuest | null>(null)

	// Form state
	const [formFirstName, setFormFirstName] = useState<string>('')
	const [formLastName, setFormLastName] = useState<string>('')
	const [formRsvpStatus, setFormRsvpStatus] = useState<'pending' | 'confirmed' | 'declined'>('pending')
	const [formNotes, setFormNotes] = useState<string>('')

	const filteredGuests = guests.filter(guest => {
		const matchesSearch = `${guest.firstName} ${guest.lastName}`
			.toLowerCase()
			.includes(searchTerm.toLowerCase())

		const matchesFilter =
			filter === 'all' ||
			(filter === 'assigned' && guest.tableId) ||
			(filter === 'unassigned' && !guest.tableId)

		return matchesSearch && matchesFilter
	})

	const openAddModal = () => {
		setEditingGuest(null)
		setFormFirstName('')
		setFormLastName('')
		setFormRsvpStatus('pending')
		setFormNotes('')
		setShowModal(true)
	}

	const openEditModal = (guest: IGuest) => {
		setEditingGuest(guest)
		setFormFirstName(guest.firstName)
		setFormLastName(guest.lastName)
		setFormRsvpStatus(guest.rsvpStatus)
		setFormNotes(guest.notes || '')
		setShowModal(true)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!authenticatedPlanner) return

		if (editingGuest) {
			// Update existing guest
			const updatedGuest: IGuest = {
				...editingGuest,
				firstName: formFirstName,
				lastName: formLastName,
				rsvpStatus: formRsvpStatus,
				notes: formNotes,
				updatedAt: new Date().toISOString()
			}

			dispatch(updateGuest(updatedGuest))
			await updateGuestEndpoint(editingGuest.id, {
				firstName: formFirstName,
				lastName: formLastName,
				rsvpStatus: formRsvpStatus,
				notes: formNotes
			}, plannerId)
		} else {
			// Create new guest
			const newGuestData = {
				plannerId: authenticatedPlanner.id,
				firstName: formFirstName,
				lastName: formLastName,
				rsvpStatus: formRsvpStatus,
				notes: formNotes
			}

			const guestId = await createGuest(newGuestData)

			if (guestId) {
				const newGuest: IGuest = {
					id: guestId,
					...newGuestData,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString()
				}
				dispatch(addGuestAction(newGuest))
			}
		}

		setShowModal(false)
	}

	const handleDelete = async (guestId: string) => {
		if (window.confirm(t('guestList.confirmDelete'))) {
			dispatch(removeGuest(guestId))
			await deleteGuestEndpoint(guestId, plannerId)
		}
	}

	// Odobrenje BRIŠE polje, ne postavlja ga na false — ista konvencija kao
	// tableId. Redux dobiva undefined, poslužitelj FieldValue.delete().
	const handleApprove = async (guest: IGuest) => {
		dispatch(updateGuest({
			...guest,
			addedByGuest: undefined,
			updatedAt: new Date().toISOString()
		}))
		await rsvpApproveGuestEndpoint(plannerId, guest.id)
	}

	const stats = {
		total: guests.length,
		confirmed: guests.filter(g => g.rsvpStatus === 'confirmed').length,
		assigned: guests.filter(g => g.tableId).length
	}

	return (
		<div ref={ref} className={`guest-list-panel ${isMobile ? 'mobile' : ''}`}>
			<div className='guest-list-header'>
				<h3>{t('guestList.title')}</h3>
				<div className='guest-stats'>
					<span>{stats.total} {t('guestList.stats.total')}</span>
					<span>•</span>
					<span>{stats.confirmed} {t('guestList.stats.confirmed')}</span>
					<span>•</span>
					<span>{stats.assigned} {t('guestList.stats.seated')}</span>
				</div>
			</div>

			<div className='guest-list-controls'>
				<input
					type='text'
					placeholder={t('guestList.search')}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className='guest-search'
				/>

				<div className='guest-filter-tabs'>
					<button
						className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
						onClick={() => setFilter('all')}
					>
						{t('guestList.filters.all')}
					</button>
					<button
						className={`filter-tab ${filter === 'unassigned' ? 'active' : ''}`}
						onClick={() => setFilter('unassigned')}
					>
						{t('guestList.filters.unassigned')}
					</button>
					<button
						className={`filter-tab ${filter === 'assigned' ? 'active' : ''}`}
						onClick={() => setFilter('assigned')}
					>
						{t('guestList.filters.assigned')}
					</button>
				</div>
			</div>

			<div className='guest-list-items'>
				{filteredGuests.length === 0
					? (
						<p className='no-guests'>
							{guests.length === 0
								? t('guestList.noGuests')
								: t('guestList.noResults')}
						</p>
					)
					: (
						filteredGuests.map(guest => (
							<DraggableGuest
								key={guest.id}
								guest={guest}
								onEdit={openEditModal}
								onDelete={handleDelete}
								onApprove={handleApprove}
								isMobile={isMobile}
							/>
						))
					)}
			</div>

			<button className='add-guest-btn' onClick={openAddModal}>
				{t('guestList.addGuest')}
			</button>

			<Modal show={showModal} onHide={() => setShowModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{editingGuest ? t('guestModal.editTitle') : t('guestModal.addTitle')}</Modal.Title>
				</Modal.Header>
				<Form onSubmit={handleSubmit}>
					<Modal.Body>
						<Form.Group className='mb-3'>
							<Form.Label>{t('guestModal.firstName')}</Form.Label>
							<Form.Control
								type='text'
								value={formFirstName}
								onChange={(e) => setFormFirstName(e.target.value)}
								required
								autoFocus
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>{t('guestModal.lastName')}</Form.Label>
							<Form.Control
								type='text'
								value={formLastName}
								onChange={(e) => setFormLastName(e.target.value)}
								required
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>{t('guestModal.status')}</Form.Label>
							<Form.Select
								value={formRsvpStatus}
								onChange={(e) => setFormRsvpStatus(e.target.value as 'pending' | 'confirmed' | 'declined')}
							>
								<option value='pending'>{t('guestModal.statusPending')}</option>
								<option value='confirmed'>{t('guestModal.statusConfirmed')}</option>
								<option value='declined'>{t('guestModal.statusDeclined')}</option>
							</Form.Select>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>{t('guestModal.notes')}</Form.Label>
							<Form.Control
								as='textarea'
								rows={2}
								value={formNotes}
								onChange={(e) => setFormNotes(e.target.value)}
								placeholder={t('guestModal.notesPlaceholder')}
							/>
						</Form.Group>
					</Modal.Body>
					<Modal.Footer>
						<Button variant='secondary' onClick={() => setShowModal(false)}>
							{t('guestModal.cancel')}
						</Button>
						<Button variant='primary' type='submit'>
							{editingGuest ? t('guestModal.save') : t('guestModal.add')}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</div>
	)
})

GuestListPanel.displayName = 'GuestListPanel'

export default GuestListPanel
